/**
 * The Eduvos 9-box vocabulary (Talent Review Playbook, "9-Box Grid" and
 * "Term sheet"). Block numbers and action labels are the playbook's, cell for
 * cell — the textbook titles ("Star", "Rough Diamond") are not used at Eduvos.
 *
 *                    Mastery (1)                Growth (2)                     Turn (3)
 *   Exceptional (3)  4 Develop in current role  2 Focus on developing …        1 Fast-track development and movement
 *   Full (2)         7 Address performance gaps 5 Develop in current role      3 Focus on developing for the future
 *   Weak / NYF (1)   9 Poor fit to role         8 Address performance gaps     6 Develop in current role
 *
 * HIPO = blocks 1, 2 and 3 (matches the backend's is_hipo rule).
 */
export type Level = 'low' | 'medium' | 'high'

export const PERFORMANCE_LABEL: Record<Level, string> = {
  high: 'Exceptional performance',
  medium: 'Full performance',
  low: 'Not yet full performance',
}

export const POTENTIAL_LABEL: Record<Level, string> = {
  high: 'Turn potential',
  medium: 'Growth potential',
  low: 'Mastery potential',
}

export const POTENTIAL_SHORT: Record<Level, string> = { high: 'Turn', medium: 'Growth', low: 'Mastery' }

/** The impact categories as the picker names them. */
// "Not yet full", never "weak": the low band is a growth message (HR process owner, 7 Sep 2026).
export const PERFORMANCE_SHORT: Record<Level, string> = { high: 'Exceptional', medium: 'Full', low: 'Not yet full' }
export const IMPACT_ORDER: Level[] = ['low', 'medium', 'high']

/** Potential, as the playbook defines it: capacity to take on bigger, more
 *  complex roles in the future. */
export const POTENTIAL_DEFINITION = 'Capacity to take on bigger, more complex roles in the future.'

export const POTENTIAL_HELP: Record<Level, string> = {
  low: 'Potential to deepen contribution within the current leadership layer.',
  medium: 'Potential for a bigger role on the same leadership layer.',
  high: 'Potential to move to the next leadership layer.',
}

/** The heading beneath each potential band on the grid. */
export const POTENTIAL_BAND: Record<Level, string> = {
  low: 'Deeper within the current leadership layer',
  medium: 'Bigger role on the same leadership layer',
  high: 'Next leadership layer',
}

/** What each impact category says about the year, on the grid's column heads. */
export const PERFORMANCE_SUBLABEL: Record<Level, string> = {
  low: 'Growth opportunity',
  medium: 'Successful impact',
  high: 'Breakthrough impact',
}

/** Mid-year rating values as stored on review sessions. */
export const MIDYEAR_RATING_LABEL: Record<string, string> = {
  exceptional: 'Exceptional',
  full: 'Full',
  not_yet_full: 'Not yet full',
  weak: 'Weak',
  tstr: 'Too soon to rate',
}

export type BlockTone = 'green' | 'amber' | 'red'

export interface EduvosBlock {
  number: number
  label: string
  tone: BlockTone
}

// The talent segments, as the HR process owner's playbook names them (7 Sep 2026).
const BLOCKS: Record<string, EduvosBlock> = {
  'high-high': { number: 1, label: 'Exceptional talent', tone: 'green' },
  'high-medium': { number: 2, label: 'High performer with potential', tone: 'green' },
  'medium-high': { number: 3, label: 'Emerging talent / rising star', tone: 'green' },
  'high-low': { number: 4, label: 'High performer', tone: 'amber' },
  'medium-medium': { number: 5, label: 'Performer with potential', tone: 'amber' },
  'low-high': { number: 6, label: 'Potential talent', tone: 'amber' },
  'medium-low': { number: 7, label: 'Solid performer', tone: 'red' },
  'low-medium': { number: 8, label: 'Potential performer', tone: 'red' },
  'low-low': { number: 9, label: 'Performance growth required', tone: 'red' },
}

/** Block for a (performance, potential) pair — `undefined` if either is missing. */
export function eduvosBlock(performance?: Level | null, potential?: Level | null): EduvosBlock | undefined {
  if (!performance || !potential) return undefined
  return BLOCKS[`${performance}-${potential}`]
}

export const isHipoBlock = (block?: EduvosBlock) => !!block && block.number <= 3

/** Block by its playbook number (1–9) — `undefined` for anything else. */
export function blockByNumber(number?: number | null): EduvosBlock | undefined {
  if (!number) return undefined
  return Object.values(BLOCKS).find((b) => b.number === number)
}

/** Soft badge classes (PlacementChecklist's block badge). One colour for the
 *  grid — colours pull placements toward the green and people take them
 *  personally (HR process owner, 7 Sep 2026); only the high-potential band keeps a light
 *  tint (Victor, 7 Sep). */
export const BLOCK_TONE_CLASS: Record<BlockTone, string> = {
  green: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  amber: 'bg-gray-100 text-gray-800 border-gray-200',
  red: 'bg-gray-100 text-gray-800 border-gray-200',
}

/** Solid chip classes — the initials circles on the talent map: one colour,
 *  a tint for blocks 1, 2 and 3. */
export const CHIP_TONE_CLASS: Record<BlockTone, string> = {
  green: 'bg-emerald-700 text-white',
  amber: 'bg-slate-600 text-white',
  red: 'bg-slate-600 text-white',
}

// ---------------------------------------------------------------------------
// Zones — the three bands of the grid, as the playbook groups the blocks:
//   Zone A (blocks 1–3)  Focus on the future
//   Zone B (blocks 4–6)  Develop in current role
//   Zone C (blocks 7–9)  Address performance gaps
// ---------------------------------------------------------------------------
export type Zone = 'A' | 'B' | 'C'

export const ZONE_LABEL: Record<Zone, string> = {
  A: 'Focus on the future',
  B: 'Develop in current role',
  C: 'Address performance gaps',
}

export const ZONE_BLOCKS: Record<Zone, string> = {
  A: 'Blocks 1, 2 and 3',
  B: 'Blocks 4 to 6',
  C: 'Blocks 7 to 9',
}

export const ZONE_TONE: Record<Zone, BlockTone> = { A: 'green', B: 'amber', C: 'red' }

/** Grid cell surface per zone: one colour, a light tint on blocks 1, 2 and 3. */
export const ZONE_CELL_CLASS: Record<Zone, string> = {
  A: 'bg-emerald-50/70 border-emerald-100',
  B: 'bg-white border-gray-200',
  C: 'bg-white border-gray-200',
}

/** Action-label text colour per zone. */
export const ZONE_TEXT_CLASS: Record<Zone, string> = {
  A: 'text-emerald-800',
  B: 'text-gray-700',
  C: 'text-gray-700',
}

/** Two ways to be off the grid, and they are not the same thing (Victor,
 *  8 Sep 2026: they rendered as the same dashed grey chip).
 *
 *  Not on the grid this year — a placement list handed in before 22 Sep 2026
 *  kept anyone too soon to rate, or without a mid-year rating, off the grid:
 *  a heavy dashed outline, closed off, with a slash. Since then such people
 *  are placed on their People Leader's judgment, so the chip is only seen on
 *  those older lists.
 *
 *  Awaiting placement — their People Leader has not submitted yet: a light
 *  solid outline, open, with a clock. They will be placed. */
export const NOT_PLACEABLE_CHIP_CLASS = 'bg-white border-2 border-dashed border-gray-500 text-gray-600'
export const NOT_PLACEABLE_STRIP_CLASS = 'bg-gray-100 border-gray-200'
export const AWAITING_CHIP_CLASS = 'bg-gray-50 border border-gray-300 text-gray-500'

/** Zone for a block (number or block object) — `undefined` when not placed. */
export function zoneOf(block?: number | EduvosBlock | null): Zone | undefined {
  const n = typeof block === 'number' ? block : block?.number
  if (!n || n < 1 || n > 9) return undefined
  if (n <= 3) return 'A'
  if (n <= 6) return 'B'
  return 'C'
}

const HONORIFICS = new Set(['dr', 'mr', 'mrs', 'ms', 'miss', 'prof', 'professor', 'adv', 'rev', 'sir'])

/** Two-letter initials for a chip: first letters of the first two words that
 *  are names ("Dr" and a bracketed nickname's bracket are skipped), upper-cased;
 *  '?' when there is nothing to work with. Mirrors the server's rule. */
export function getInitials(name?: string | null): string {
  const words = (name || '')
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/^[()[\]"'.,]+|[()[\]"'.,]+$/g, ''))
    .filter((w) => w && !HONORIFICS.has(w.toLowerCase()))
  if (words.length === 0) return '?'
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0])
    .join('')
    .toUpperCase()
}

// ---------------------------------------------------------------------------
// Tier 2 vocabulary — what blocks 1, 2 and 3 and key persons carry beyond the
// placement: readiness for the next role, the 70/20/10 plan, retention risk.
// ---------------------------------------------------------------------------
export type Readiness = 'ready-now' | 'ready-1-year' | 'ready-2-plus'

export const READINESS_ORDER: Readiness[] = ['ready-now', 'ready-1-year', 'ready-2-plus']

/** The client's readiness vocabulary (the HR process owner's process notes, 7 Sep 2026). */
export const READINESS_LABEL: Record<Readiness, string> = {
  'ready-now': 'Ready now',
  'ready-1-year': 'Planned (1–3 years)',
  'ready-2-plus': 'Potential (3+ years)',
}

export type RetentionRiskLevel = 'low' | 'medium' | 'high' | 'critical'

export const RETENTION_RISK_ORDER: RetentionRiskLevel[] = ['low', 'medium', 'high', 'critical']

export const RETENTION_RISK_LABEL: Record<RetentionRiskLevel, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

export type PlanLineKey = 'experience' | 'exposure' | 'education'

/** The 70/20/10 development plan, one line each. */
export const PLAN_LINES: { key: PlanLineKey; label: string; hint: string }[] = [
  { key: 'experience', label: '70% on the job', hint: 'stretch work, a project, an acting role' },
  { key: 'exposure', label: '20% through others', hint: 'coaching, mentoring, shadowing' },
  { key: 'education', label: '10% formal learning', hint: 'a course, a qualification, a programme' },
]

// ---------------------------------------------------------------------------
// Impact history — the formal ratings on record, by period
// ---------------------------------------------------------------------------
export const RATING_PERIOD_LABEL: Record<string, string> = { midyear: 'Mid-year', yearend: 'Year-end' }
export const RATING_PERIOD_SHORT: Record<string, string> = { midyear: 'MY', yearend: 'YE' }

// ---------------------------------------------------------------------------
// Reflection questions — the HR process owner's list (7 Sep 2026), verbatim. One list for every
// placement, not per block: the People Leader reviews each placement against
// it once the team is on the grid. Shown in every block's guidance popover and
// from the checklist header; the key-people question also sits on the
// nomination box.
// ---------------------------------------------------------------------------
export interface ReflectionGroup {
  title: string
  lead: string
  questions: string[]
}

export const REFLECTION_QUESTIONS: ReflectionGroup[] = [
  {
    title: 'Impact',
    lead: 'What have they delivered?',
    questions: [
      'Is this person a sustained high performer — Full or Exceptional Impact over time, ideally across three years and relative to peers in comparable roles?',
      'What evidence supports this Impact assessment?',
      'How does this person compare with the strongest talent at this leadership layer across Eduvos?',
      'How would this person compare with the best talent in our industry, not only the best within Eduvos?',
    ],
  },
  {
    title: 'Potential',
    lead: 'What could they do next?',
    questions: [
      'Does this person demonstrate the capabilities and behaviours required to succeed at a higher leadership layer at Eduvos?',
      'What have they demonstrated beyond the requirements of their current role or leadership layer? Is there evidence of additional capacity?',
      'Do they have the ambition to advance, and have they indicated a willingness to make the commitments that progression may require?',
      'Do they meet, or could they reasonably attain, the minimum requirements for the next leadership layer, including relevant qualifications?',
      'Are we seeing genuine future capacity — or confusing hard work, loyalty, ambition or visibility with Potential?',
    ],
  },
  {
    title: 'Growth Plan',
    lead: 'What will accelerate their growth?',
    questions: [
      "What are the person's most important development gaps in relation to their current or future role?",
      'What is the single most important experience that would accelerate their growth?',
      'What capabilities or skills need to be strengthened, and which development experiences, coaching or other interventions would address these gaps most effectively?',
    ],
  },
  {
    title: 'Key people',
    lead: 'Would we fight to keep them?',
    questions: ['If this person left Eduvos, how hard would we fight to keep them — and why?'],
  },
]

/** The key-people question, for the nomination box. */
export const KEY_PERSON_QUESTION = REFLECTION_QUESTIONS[3].questions[0]

/** The prompt on the why box. It used to carry the HR process owner's whole definition of
    "critical to keep" (7 Sep 2026), which read as a paragraph of instructions
    inside the box the People Leader was trying to write in; the label above it
    already asks the question, so the box asks for one thing (HR process owner, 9 Sep 2026). */
export const KEY_PERSON_HINT = 'Motivation'

// ---------------------------------------------------------------------------
// The nine talent segments — the HR process owner's playbook content (7 Sep 2026), verbatim
// but for two house-style edits: her "Box" reads "block" as the rest of the
// platform does, and -ise spellings.
// ---------------------------------------------------------------------------
export interface BlockGuidanceContent {
  /** The segment name, e.g. 'Exceptional talent'. */
  name: string
  /** Blocks 1, 2 and 3 — the high-potentials. */
  hipo: boolean
  /** What this block means. */
  summary: string
  /** What to do about it — the playbook's instruction. */
  action: string
  /** The fuller guidance behind the action. */
  detail: string
}

export const BLOCK_GUIDANCE: Record<number, BlockGuidanceContent> = {
  1: {
    name: 'Exceptional talent',
    hipo: true,
    summary: 'Exceptional performer ready to move to the next leadership layer.',
    action: 'Move now',
    detail:
      'The Star. Ready for an assignment at a higher level. Restless, vulnerable to head-hunters. Need rewards commensurate with the new challenge. Keep them learning while maintaining a fair deal.',
  },
  2: {
    name: 'High performer with potential',
    hipo: true,
    summary: 'Exceptional performer capable of continued growth along the current path.',
    action: 'Develop for the next layer or promote on the same layer',
    detail:
      'Give stretch assignments at the same leadership level in order to start acquiring the skills, values and time applications of the next level. Give them a challenge off the People Leader\u2019s desk, rather let them fail now than later.',
  },
  3: {
    name: 'Emerging talent / rising star',
    hipo: true,
    summary:
      'Full performer with turn potential who should further improve performance before being asked to make a leadership turn.',
    action: 'Stretch performance',
    detail:
      'Focus on improving their performance. Ask more of them than their peers, encourage innovation to get there. The situation may be holding them back, or they may belong in block 5.',
  },
  4: {
    name: 'High performer',
    hipo: false,
    summary:
      'Exceptional performer likely to remain at the current level, but whose contributions should be recognised.',
    action: 'Reward and recognise',
    detail:
      'The Seasoned Pro. Should remain at their level but need to be recognised. Development? But should be involved in mentoring. Resist the tendency to take them for granted, as they may withdraw their performance.',
  },
  5: {
    name: 'Performer with potential',
    hipo: false,
    summary:
      'Should be considered for bigger jobs along the current path if they can deliver better results.',
    action: 'Stretch performance',
    detail:
      'If they achieve stretch goals, they could jump to block 2. Managers often load this block with people who really belong in block 8 or 7.',
  },
  6: {
    name: 'Potential talent',
    hipo: false,
    summary: 'Recently moved or promoted to a new job; requires some time and experience.',
    action: 'Stretch performance, wait and see',
    detail:
      'Often people who have just been promoted and just need time. This is often where block 1 people get moved to. If not, there may be a \u2018fatal flaw\u2019.',
  },
  7: {
    name: 'Solid performer',
    hipo: false,
    summary: 'Full performer not likely to grow beyond the scope of the current position.',
    action: 'Stretch performance',
    detail:
      'Could go either way — could become valuable in their current role, or could slip to block 9 if they are de-motivated.',
  },
  8: {
    name: 'Potential performer',
    hipo: false,
    summary: 'Performs parts of the job well, other parts poorly.',
    action: 'Get the whole job done',
    detail:
      'Need tight performance management. They cannot afford any more slipping. This rating indicates that something is wrong: they have the skills but are not applying them.',
  },
  9: {
    name: 'Performance growth required',
    hipo: false,
    summary: 'Frequently working at the wrong leadership level.',
    action: 'Performance manage tightly and coach for improved performance',
    detail:
      'Often working at the wrong leadership level — demote or ask to leave. Can clog pipelines. Don\u2019t immediately write them off. Examine whether management or the organisation are inhibiting performance, along with factors such as poor job definition, insufficient resources or lack of integration.',
  },
}
